import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useState} from 'react';

import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {BioentitySummary} from 'folder-items/FileConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import AnalysisTemplatesEditableField from 'instruments/analysis-templates/AnalysisTemplatesEditableField';
import * as ConnectFilesDAO from 'instruments/connections/ConnectFilesDAO';
import {
  InstrumentServiceDetails,
  ConnectionStatusResponse,
  ConnectionStatus,
  ConnectionFieldType,
  AdapterField,
} from 'instruments/connections/ConnectionConstants';
import {
  bulkArchive,
  bulkUnarchive,
  getAdapter,
  getInstrumentServiceDetails,
  startCredentialTest,
  updateConnection,
} from 'instruments/connections/ConnectionDAO';
import ConnectionFieldsForm from 'instruments/connections/ConnectionFieldsForm';
import {
  buildTagUpdateObject,
  getInitialValues,
  applyAutoSyncLogic,
} from 'instruments/connections/ConnectionFieldsUtils';
import ConnectionStatusDisplay from 'instruments/connections/ConnectionStatusDisplay';
import {SnowflakeExportModal} from 'instruments/connections/SnowflakeExportModal';
import {EquipmentEditableField} from 'instruments/equipment/EquipmentPicker';
import {InstrumentQueryStatus, isSnowflakeAdapter} from 'instruments/instrument-data/InstrumentDataConstants';
import {pollInstrumentQueryStatus} from 'instruments/instrument-data/InstrumentDataDAO';
import {InstrumentGateway} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {getGateway} from 'instruments/instrument-gateways/InstrumentGatewayDAO';
import {GatewayStatusIcon} from 'instruments/instrument-gateways/InstrumentGatewaysPage';
import AliasField from 'metadata/AliasField';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import * as BlobActions from 'schemas/tags/BlobActions';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';
import BlobInputModal from 'schemas/tags/BlobInputModal';
import {ValidationInfo} from 'schemas/tags/EditableSchemaTag';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Title} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import legacyRunModal from 'ui-library/legacyRunModal';

import {ConnectionQuery, ConnectionQueryVariables} from './__generated/ConnectionQuery';
import ConnectionCollaboratorsModal from './ConnectionCollaboratorsModal';
import DisableConnectionButton from './DisableConnectionButton';

export const CONNECTION_QUERY: DocumentNode = gql`
  query ConnectionQuery($connectionId: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    connection(id: $connectionId) {
      ... on ConnectionNode {
        id
        name
        humanId
        permissions {
          readable
          writable
          archivable
          unarchivable
        }
        adapterId
        creator {
          id
          name
          handle
          avatarUrl
        }
        dataCatalogFolder {
          id
        }
        createdAt
        fields
        ...HasArchiveRecordGraphQL
        associatedEquipment {
          ...BioentitySummaryGraphQL
        }
        analysisTemplates {
          id
          name
          archiveRecord {
            purpose
          }
          configVariables {
            id
            configVariableKey
          }
          dataFrameVariables {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('BioentitySummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type Connection = ExtractByTypename<ConnectionQuery['connection'], 'ConnectionNode'>;

export interface ConnectionDetailsProps {
  connection: Connection;
  refetch: () => void;
  refreshDetails: () => void;
  gateway: InstrumentGateway | null;
  status: ConnectionStatusResponse | null;
  adapterDisplayName: string | null;
  adapterFields: Array<AdapterField>;
  fieldValues: Record<string, string>;
  fieldInfos: {[id: string]: string | null};
  fieldValidationInfos: {[id: string]: ValidationInfo};
  fieldTypes: {[id: string]: string | null};
  allotropyVendor: string | null;
  instrumentServiceDetails: InstrumentServiceDetails | null;
  onClearFieldValidation: (fieldNames: Array<string>) => void;
  onFieldValuesChange: (newValues: Record<string, string>) => void;
}

export function ConnectionDetails(props: ConnectionDetailsProps): JSX.Element {
  const connection = props.connection;
  const connectionId = connection.id;
  const gateway = props.gateway;
  const [conversionStatus, setConversionStatus] = useState(null as string | null);
  const [blob, setBlob] = useState<BenchlingBlob | null>(null);
  const [credentialTestStatus, setCredentialTestStatus] = useState<'SUCCESS' | 'FAILURE' | 'TESTING' | null>(
    null
  );

  const _handleUpdateIsEnabled = async (isEnabled: boolean): Promise<void> => {
    await updateConnection(connectionId, {isEnabled});
    props.refetch();
  };

  const handleFieldChange = async (fieldId: string, value: string): Promise<void> => {
    const updatedValues = applyAutoSyncLogic(fieldId, value, props.fieldValues);

    // Build TagUpdateObject only with the fields that actually changed
    const changedValues: Record<string, string> = {};
    changedValues[fieldId] = value;
    // If auto-sync was disabled, include it too
    if (updatedValues.enable_auto_sync !== props.fieldValues.enable_auto_sync) {
      changedValues.enable_auto_sync = updatedValues.enable_auto_sync;
    }

    // Clear validation for all fields being updated
    props.onClearFieldValidation(Object.keys(changedValues));

    // Update local state with trimmed values to match what's sent to the server
    const tagsToSet = buildTagUpdateObject(changedValues);
    const trimmedValues = {...updatedValues};
    for (const [key, tag] of Object.entries(tagsToSet)) {
      trimmedValues[key] = tag.value ?? '';
    }
    props.onFieldValuesChange(trimmedValues);

    await updateConnection(connectionId, {tags: tagsToSet, gatewayType: gateway!.gatewayType});
    props.refreshDetails();
  };

  const _handleUpdateDataCatalogFolderId = async (e: {target: {value: string}}): Promise<void> => {
    await updateConnection(connectionId, {dataCatalogFolderId: e.target.value});
    props.refetch();
  };

  const _handleUpdateEquipment = async (equipment: Array<BioentitySummary>): Promise<void> => {
    await updateConnection(connectionId, {equipmentIds: equipment.map((item) => item.id)});
    props.refetch();
  };

  const _handleUpdateAnalysisTemplates = async (
    templates: Array<AnalysisTemplateSelectorTemplate>
  ): Promise<void> => {
    await updateConnection(connectionId, {analysisTemplateIds: templates.map((template) => template.id)});
    props.refetch();
  };

  const onUploadFiles = (files: Array<File>): JQueryPromise<void> =>
    BlobActions.uploadFilesAsBlobs(files, {
      shouldGeneratePreviewFile: false,
      onBlobsCreated: async (newBlobs) => {
        setConversionStatus('LOADING');
        if (props.allotropyVendor == null || newBlobs.length === 0) {
          return;
        }
        setBlob(newBlobs[0]);
        let blobId = newBlobs[0].id;
        try {
          blobId = (await ConnectFilesDAO.convertToAsm(blobId, props.allotropyVendor)).blobId;
          await ConnectFilesDAO.convertToCsv(blobId);
          setConversionStatus('SUCCESS');
        } catch (err) {
          if (err.status === 400) {
            setConversionStatus('FAILURE');
          } else {
            throw err;
          }
        }
      },
    }).done(() => {});

  const getBlobStatus = (): React.JSX.Element => {
    switch (conversionStatus) {
      case 'SUCCESS':
        return (
          <div>
            <BenchlingIcon
              className={classNames(css(styles.colorGreen), {
                'u-mini-spacing-left': true,
                'u-mini-spacing-right': true,
              })}
              icon="done"
            />
            <span>File is compatible!</span>
          </div>
        );
      case 'FAILURE':
        return (
          <div>
            <BenchlingIcon
              className={classNames(css(styles.colorRedDark), {
                'u-mini-spacing-left': true,
                'u-mini-spacing-right': true,
              })}
              icon="warning"
            />
            <span>File is not compatible.</span>
          </div>
        );
      default:
        return (
          <div>
            <BenchlingIcon
              className={classNames(css(styles.colorGrayDark), {
                'u-mini-spacing-left': true,
                'u-mini-spacing-right': true,
              })}
              icon="hourglass-half"
            />
            <span>Testing...</span>
          </div>
        );
    }
  };

  const getCredentialTestStatus = (): React.JSX.Element | null => {
    switch (credentialTestStatus) {
      case 'SUCCESS':
        return (
          <div>
            <BenchlingIcon
              className={classNames(css(styles.colorGreen), {
                'u-mini-spacing-left': true,
                'u-mini-spacing-right': true,
              })}
              icon="done"
            />
            <span>Credentials are valid!</span>
          </div>
        );
      case 'FAILURE':
        return (
          <div>
            <BenchlingIcon
              className={classNames(css(styles.colorRedDark), {
                'u-mini-spacing-left': true,
                'u-mini-spacing-right': true,
              })}
              icon="warning"
            />
            <span>Invalid credentials. Please check username and password.</span>
          </div>
        );
      case 'TESTING':
        return (
          <div>
            <BenchlingIcon
              className={classNames(css(styles.colorGrayDark), {
                'u-mini-spacing-left': true,
                'u-mini-spacing-right': true,
              })}
              icon="hourglass-half"
            />
            <span>Testing credentials...</span>
          </div>
        );
      default:
        return null;
    }
  };

  const handleTestCredentials = async (): Promise<void> => {
    setCredentialTestStatus('TESTING');
    try {
      const {instrumentQueryId} = await startCredentialTest(connectionId);
      const queryResult = await pollInstrumentQueryStatus(instrumentQueryId);
      setCredentialTestStatus(queryResult.status === InstrumentQueryStatus.DONE ? 'SUCCESS' : 'FAILURE');
    } catch (err) {
      // HTTP errors (4xx/5xx) indicate expected failures like invalid credentials or missing config.
      // Show failure status for these. Rethrow unexpected errors.
      if (err.status) {
        setCredentialTestStatus('FAILURE');
      } else {
        throw err;
      }
    }
  };

  // TODO: Currently only Chromeleon adapters support credential testing because other adapters
  // that require login do not have a BROWSE_FILES command. We will add a VERIFY_LOGIN command
  // for this purpose and update the verification API to use that, after which this Chromeleon
  // restriction can be removed.
  const isChromeleonAdapter = props.instrumentServiceDetails?.adapterId === 'chromeleon-adapter';
  const showSnowflakeExport =
    BenchlingConfig.SNOWFLAKE_GATEWAY_ENABLED &&
    isSnowflakeAdapter(props.instrumentServiceDetails?.adapterId);
  const hasCredentialFields =
    props.fieldTypes.username !== undefined && props.fieldTypes.password !== undefined;
  const hasCredentialsForAutoSync = (() => {
    if (props.fieldValues.per_user_login_required !== 'true') {
      return true;
    }
    return Boolean(props.fieldValues.username?.trim() && props.fieldValues.password?.trim());
  })();
  const hasActualCredentials = Boolean(
    props.instrumentServiceDetails?.fields?.username?.value?.trim() &&
    props.instrumentServiceDetails?.fields?.password?.value?.trim()
  );
  const showTestCredentials = isChromeleonAdapter && hasCredentialFields;

  const permissions = connection.permissions;
  const isWritable = permissions.writable;
  const statusValue = props.status?.value;

  return (
    <div className="react-tab-container">
      <div>
        <div>
          <Row bufferRowReset={true}>
            <Col xs={6}>
              <AliasField
                initialAlias={connection.name}
                isFullWidth={true}
                isHeader={true}
                isWritable={isWritable}
                complete={async (name: string) => {
                  await updateConnection(connectionId, {name});
                  props.refetch();
                }}
                placeholder="Name"
              />
              <ArchiveRecordLabel item={{...connection, archiveRecord: connection.archiveRecord}} />
            </Col>
            <Col xs={6}>
              <div className="u-justify-content-end">
                <ArchiveOrUnarchiveButton
                  className="metadata-header-button-spacing-left"
                  isArchivable={Boolean(permissions.archivable)}
                  isUnarchivable={Boolean(permissions.unarchivable)}
                  item={connection}
                  getAnalyticsMetadata={() => ({
                    actionOrigin: ActionOrigin.CONNECTION_PAGE,
                  })}
                  onArchive={async (ids: Array<string>, purpose: string) => {
                    await bulkArchive(ids, purpose);
                    props.refetch();
                  }}
                  onUnarchive={async (ids: Array<string>) => {
                    await bulkUnarchive(ids);
                    props.refetch();
                  }}
                />
                <DisableConnectionButton status={props.status} updateIsEnabled={_handleUpdateIsEnabled} />
                {showSnowflakeExport && (
                  <Button
                    className="metadata-header-button-spacing-left"
                    buttonStyle={ButtonStyle.TERTIARY_BLUE}
                    size={ButtonSize.SMALL}
                    text="Export table to dataset"
                    onClick={() =>
                      legacyRunModal(SnowflakeExportModal, {
                        connectionId,
                        connectionName: connection.name,
                        onRequestClose: props.refetch,
                      })
                    }
                  />
                )}
                <ExportAuditLogButton className="metadata-header-button-spacing-left" ids={[connectionId]} />
                <Button
                  className="metadata-header-button-spacing-left"
                  icon="settings"
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  size={ButtonSize.SMALL}
                  onClick={() => legacyRunModal(ConnectionCollaboratorsModal, {connectionId})}
                />
              </div>
            </Col>
          </Row>
        </div>
        <div>
          <Row bufferRowReset={true}>
            {statusValue ? (
              <Col xs={12}>
                <ConnectionStatusDisplay status={statusValue} />
              </Col>
            ) : null}
          </Row>
        </div>
      </div>
      <div className="u-spacing-top">
        <FormGroup>
          <Row bufferRowReset={true}>
            <Col xs={6}>
              <Title>Creator</Title>
              <div>
                <Avatar
                  className="u-mini-spacing-right"
                  size={AvatarSize.XXSMALL}
                  entity={connection.creator}
                />
                {connection.creator.name}
              </div>
            </Col>
            <Col xs={6}>
              <Title>Type</Title>
              <div>{props.adapterDisplayName}</div>
            </Col>
          </Row>
        </FormGroup>
        <div className="u-spacing-top">
          <FormGroup>
            <Row bufferRowReset={true}>
              <Col xs={6}>
                <Title>Benchling Gateway</Title>
                <div>
                  {props.gateway && <GatewayStatusIcon gateway={props.gateway} />} {props.gateway?.name}
                </div>
              </Col>
              <Col xs={6}>
                <Title>Benchling Location</Title>
                <FolderSelector
                  value={connection.dataCatalogFolder?.id}
                  onChange={_handleUpdateDataCatalogFolderId}
                  showInventory={true}
                  readOnly={!isWritable}
                  unsetOnFetchFail={false}
                />
              </Col>
            </Row>
          </FormGroup>
        </div>
        <div className="u-spacing-top">
          <ConnectionFieldsForm
            fields={props.adapterFields}
            values={props.fieldValues}
            fieldInfos={props.fieldInfos}
            fieldValidationInfos={props.fieldValidationInfos}
            isWritable={isWritable}
            isCreating={false}
            hasCredentialsForAutoSync={hasCredentialsForAutoSync}
            onChange={handleFieldChange}
          />
        </div>
        {BenchlingConfig.ENABLE_EQUIPMENT && (
          <div className="u-spacing-top">
            <FormGroup>
              <Row bufferRowReset={true}>
                <Col xs={6}>
                  <Title>Associated Equipment</Title>
                  <EquipmentEditableField
                    shouldAllowMultiple={true}
                    initialSelection={props.connection.associatedEquipment.map((item) =>
                      graphQLToREST('BioentitySummary', item)
                    )}
                    onComplete={_handleUpdateEquipment}
                    isWriteable={true}
                  />
                </Col>
              </Row>
            </FormGroup>
          </div>
        )}
        {BenchlingConfig.ENABLE_ANALYSIS_TOOL_TEMPLATES &&
          BenchlingConfig.PIPELINES_ENABLE_CONNECTION_INTEGRATION && (
            <div className="u-spacing-top">
              <FormGroup>
                <Row bufferRowReset={true}>
                  <Col xs={6}>
                    <Title>Automation templates</Title>
                    <AnalysisTemplatesEditableField
                      initialSelection={(props.connection.analysisTemplates || []).map((template) => ({
                        ...template,
                        configVariables: template.configVariables.map((cv) => ({
                          ...cv,
                          name: cv.configVariableKey,
                          variableTypeConfigSpec: {kind: 'string', required: true} as const,
                        })),
                      }))}
                      onComplete={_handleUpdateAnalysisTemplates}
                      isWritable={isWritable}
                    />
                  </Col>
                </Row>
              </FormGroup>
            </div>
          )}
        {showTestCredentials && (
          <FormGroup>
            <Row bufferRowReset={true}>
              <Col sm={12}>
                <FieldLabel label="Test saved credentials" labelStyle={LabelStyle.LABEL} />
                <HelpButton
                  renderContent={() => 'Validate the stored credentials against the adapter'}
                  iconClassName="u-mini-spacing-left"
                />
                <div className={css(styles.button)}>
                  <Tooltip
                    placement={FloatingPlacement.TOP}
                    text="Username and password fields must be set to validate credentials"
                    isDisabled={hasActualCredentials}
                  >
                    <Button
                      buttonStyle={ButtonStyle.TERTIARY_GRAY}
                      size={ButtonSize.MINI}
                      fullWidth={false}
                      text="Test credentials"
                      icon="key"
                      disabled={!hasActualCredentials || credentialTestStatus === 'TESTING'}
                      textClassName="button-text--nowrap"
                      onClick={handleTestCredentials}
                    />
                  </Tooltip>
                </div>
                <div className={css(styles.credentialTestStatus)}>{getCredentialTestStatus()}</div>
              </Col>
            </Row>
          </FormGroup>
        )}
        {props.allotropyVendor ? (
          <FormGroup>
            <Row bufferRowReset={true}>
              <Col sm={12}>
                <FieldLabel label="Test file compatibility for Connection" labelStyle={LabelStyle.LABEL} />
                <div className={css(styles.button)}>
                  <Button
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    size={ButtonSize.MINI}
                    fullWidth={false}
                    text="Choose file"
                    icon="upload"
                    disabled={false}
                    textClassName="button-text--nowrap"
                    onClick={() => {
                      legacyRunModal(BlobInputModal, {
                        allowMultiple: false,
                        onUploadFiles,
                      });
                    }}
                  />
                </div>
              </Col>
            </Row>
            {blob ? (
              <Row>
                <Col sm={12}>
                  <div className={css(styles.chip)}>
                    <Chip
                      className="chip-container"
                      icon="paperclip"
                      size={ChipSize.MINI}
                      text={blob.name}
                      href={blob.url}
                      onRemove={() => {
                        setBlob(null);
                        setConversionStatus(null);
                      }}
                    />
                  </div>
                  {getBlobStatus()}
                </Col>
              </Row>
            ) : null}
          </FormGroup>
        ) : null}
      </div>
    </div>
  );
}

const CONNECTION_POLL_INTERVAL_SEC = 10;

export default function QueryingConnectionDetails(props: {connectionId: string}): JSX.Element {
  const connectionId = props.connectionId;
  const result = useBenchlingQuery<ConnectionQuery, ConnectionQueryVariables>(CONNECTION_QUERY, {
    variables: {connectionId},
  });

  const [instrumentServiceDetails, setInstrumentServiceDetails] = useState(
    null as InstrumentServiceDetails | null
  );
  const [gateway, setGateway] = useState(null as InstrumentGateway | null);
  const [adapterDisplayName, setAdapterDisplayName] = useState<string | null>(null);
  const [adapterFields, setAdapterFields] = useState<Array<AdapterField>>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldInfos, setFieldInfos] = useState({} as {[id: string]: string | null});
  const [fieldValidationInfos, setFieldValidationInfos] = useState({} as {[id: string]: ValidationInfo});
  const [fieldTypes, setFieldTypes] = useState({} as {[id: string]: string | null});
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const fetchDetails = useCallback(async (): Promise<void> => {
    try {
      const details = await getInstrumentServiceDetails(connectionId);
      setInstrumentServiceDetails(details);
      const gw = await getGateway(details.gatewayId);
      setGateway(gw);
      const adapter = await getAdapter(details.instrumentName, gw.gatewayType);
      setAdapterDisplayName(details.adapterDisplayName ?? details.instrumentName);
      const displayableFields = adapter.fields.filter(
        (field) =>
          field.fieldType === ConnectionFieldType.TEXT || field.fieldType === ConnectionFieldType.SECRET_REF
      );
      setAdapterFields(displayableFields);
      setFieldValues(getInitialValues(displayableFields, details));
      setFieldInfos(Object.fromEntries(adapter.fields.map((field) => [field.id, field.info])));
      setFieldTypes(
        Object.fromEntries(
          adapter.fields.map((field) => [field.id, field.fieldType ? field.fieldType : null])
        )
      );
      setFieldValidationInfos(
        Object.keys(details.fieldValidationInfoByName).length
          ? details.fieldValidationInfoByName
          : Object.fromEntries(adapter.fields.map((field) => [field.id, {name: field.id}]))
      );
    } finally {
      setHasFetched(true);
    }
  }, [connectionId]);

  // Lightweight poll: only refreshes connection details (status + validation).
  // Gateway and adapter are static and don't need re-fetching.
  const pollDetails = useCallback(async (): Promise<void> => {
    try {
      const details = await getInstrumentServiceDetails(connectionId);
      setInstrumentServiceDetails(details);
      if (Object.keys(details.fieldValidationInfoByName).length) {
        setFieldValidationInfos(details.fieldValidationInfoByName);
      }
    } catch (err) {
      // HTTP errors are expected during transient network issues — the next poll will retry.
      // Rethrow unexpected non-HTTP errors.
      if (!err.status) {
        throw err;
      }
    }
  }, [connectionId]);

  useEffect((): void => {
    fetchDetails();
  }, [fetchDetails]);

  useEffect(() => {
    if (!BenchlingConfig.INSTRUMENTS_ENABLE_CONNECTION_DETAILS_POLLING) {
      return;
    }
    const intervalId = setInterval(pollDetails, CONNECTION_POLL_INTERVAL_SEC * 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [pollDetails]);

  if (result.type !== QueryResultType.SUCCESS) {
    return (
      <div className="react-tab-container">
        <LoadingFileScreen />
      </div>
    );
  }

  const connection = result.data.connection;
  if (isInaccessible(connection)) {
    return (
      <div className="react-tab-container">
        <WorkspaceItemInaccessibleGuard inaccessibleInfo={connection} modelType="connection" />
      </div>
    );
  }

  const status = hasFetched
    ? instrumentServiceDetails?.id != null
      ? instrumentServiceDetails.status
      : {
          value: ConnectionStatus.SERVICE_UNAVAILABLE,
          timestamp: new Date().toISOString(),
        }
    : null;
  return (
    <div className="react-tab-container">
      <ConnectionDetails
        connection={connection}
        refetch={() => {
          fetchDetails();
          result.refetch({connectionId});
        }}
        refreshDetails={pollDetails}
        gateway={gateway}
        status={status}
        adapterDisplayName={adapterDisplayName}
        adapterFields={adapterFields}
        fieldValues={fieldValues}
        fieldInfos={fieldInfos}
        fieldValidationInfos={fieldValidationInfos}
        fieldTypes={fieldTypes}
        allotropyVendor={instrumentServiceDetails ? instrumentServiceDetails.allotropyVendor || null : null}
        instrumentServiceDetails={instrumentServiceDetails}
        onClearFieldValidation={(fieldNames: Array<string>) => {
          setFieldValidationInfos((prev) => {
            const next = {...prev};
            for (const name of fieldNames) {
              next[name] = {name};
            }
            return next;
          });
        }}
        onFieldValuesChange={setFieldValues}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  button: {
    marginTop: 6,
    flexShrink: 0,
  },
  colorGrayDark: {
    color: Colors.GRAY_DARK,
  },
  colorGreen: {
    color: Colors.GREEN,
  },
  colorRedDark: {
    color: Colors.RED_DARK,
  },
  chip: {
    marginTop: 6,
  },
  credentialTestStatus: {
    marginTop: 8,
  },
});
